import React, { useEffect, useState } from 'react';
import { FolderOpen, FileText, Download, ExternalLink, Loader2, File as FileIcon, Image as ImageIcon, FileSpreadsheet, Folder } from 'lucide-react';
import AccessGate from '../components/AccessGate';
import { useAuth } from '../context/AuthContext';

const PAGE_KEY = 'workspace-documents';

function iconFor(mime) {
  if (!mime) return FileIcon;
  if (mime.includes('folder')) return Folder;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return FileSpreadsheet;
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime.includes('pdf') || mime.includes('document') || mime.includes('text')) return FileText;
  return FileIcon;
}

function humanSize(bytes) {
  if (!bytes) return '';
  const b = Number(bytes);
  if (Number.isNaN(b)) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export default function DocumentsLibrary() {
  const { getGoogleDocuments } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await getGoogleDocuments();
        if (alive) setData(res);
      } catch (e) {
        if (alive) setError('Could not load the document library right now.');
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AccessGate pageKey={PAGE_KEY}>
      <div className="max-w-4xl mx-auto px-4 py-10 fade-in" data-testid="documents-page">
        <div className="flex items-center gap-3 mb-2">
          <FolderOpen size={28} style={{ color: 'var(--maroon)' }} />
          <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>Document Library</h1>
        </div>
        <div className="h-1 w-20 mb-6" style={{ background: 'var(--gold)' }} />

        {loading && (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--olive)' }} data-testid="documents-loading">
            <Loader2 className="animate-spin" size={16} /> Loading documents…
          </div>
        )}

        {!loading && error && (
          <p className="text-sm" style={{ color: '#8a1f11' }} data-testid="documents-error">{error}</p>
        )}

        {!loading && !error && data && !data.configured && (
          <div className="bg-white rounded-lg p-6 shadow-sm border text-sm" style={{ borderColor: 'rgba(0,0,0,0.08)', color: 'var(--olive)' }} data-testid="documents-not-configured">
            The document library hasn’t been set up yet. Please check back soon.
          </div>
        )}

        {!loading && !error && data && data.configured && data.sources.length === 0 && (
          <div className="bg-white rounded-lg p-6 shadow-sm border text-sm" style={{ borderColor: 'rgba(0,0,0,0.08)', color: 'var(--olive)' }} data-testid="documents-empty">
            There are no document folders available for your membership yet.
          </div>
        )}

        {!loading && !error && data && data.sources.map((src) => (
          <section key={src.id} className="mb-8" data-testid={`documents-source-${src.id}`}>
            <h2 className="font-title text-xl mb-3 pb-1 border-b" style={{ color: 'var(--navy)', borderColor: 'var(--gold)' }}>{src.label}</h2>
            {src.error && <p className="text-sm" style={{ color: '#8a1f11' }}>{src.error}</p>}
            {!src.error && src.files.length === 0 && (
              <p className="text-sm" style={{ color: 'var(--olive)' }}>This folder is empty.</p>
            )}
            <ul className="space-y-2">
              {src.files.map((f) => {
                const Icon = iconFor(f.mimeType);
                return (
                  <li key={f.id} className="flex items-center justify-between gap-3 bg-white rounded-lg p-3 shadow-sm border" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`document-file-${f.id}`}>
                    <span className="flex items-center gap-3 min-w-0">
                      <Icon size={20} className="flex-shrink-0" style={{ color: 'var(--maroon)' }} />
                      <span className="min-w-0">
                        <span className="block text-sm truncate">{f.name}</span>
                        {f.size && <span className="block text-xs" style={{ color: 'var(--olive)' }}>{humanSize(f.size)}</span>}
                      </span>
                    </span>
                    <span className="flex items-center gap-3 flex-shrink-0">
                      {f.webViewLink && (
                        <a href={f.webViewLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: 'var(--navy)' }}>
                          <ExternalLink size={14} /> View
                        </a>
                      )}
                      {f.webContentLink && (
                        <a href={f.webContentLink} className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: 'var(--navy)' }}>
                          <Download size={14} /> Download
                        </a>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </AccessGate>
  );
}
